// UTF-16 offset to line and column conversion, shared by every module in
// this directory that turns one of the native tooling build's offsets into
// Monaco's 1-based positions. Every offset the native build answers
// (diagnostic spans, hover ranges, definitions, outline ranges) counts
// UTF-16 code units, exactly what `String.prototype.charCodeAt` indexes, so a
// JS string needs no surrogate-pair conversion in either direction. Kept
// free of any `monaco-editor` import for the reason `tokenize.ts` explains.

export interface TextPosition {
  /** 1-based. */
  readonly line: number;
  /** 1-based, in UTF-16 code units from the start of the line. */
  readonly column: number;
}

/** A UTF-16 code unit offset into `text` as a 1-based line and column.
 * An offset outside the text is clamped to it. */
export function utf16PositionAt(text: string, offset: number): TextPosition {
  const clamped = Math.max(0, Math.min(offset, text.length));
  let line = 1;
  let lineStart = 0;
  for (let i = 0; i < clamped; i++) {
    if (text.charCodeAt(i) === 10 /* \n */) {
      line += 1;
      lineStart = i + 1;
    }
  }
  return { line, column: clamped - lineStart + 1 };
}

/** The inverse of `utf16PositionAt`: a 1-based line and column as a UTF-16
 * offset into `text`, clamped to the text (and to the line's own end). */
export function utf16OffsetAt(text: string, line: number, column: number): number {
  let lineStart = 0;
  for (let current = 1; current < line; current++) {
    const next = text.indexOf('\n', lineStart);
    if (next === -1) return text.length;
    lineStart = next + 1;
  }
  const lineEnd = text.indexOf('\n', lineStart);
  const limit = lineEnd === -1 ? text.length : lineEnd;
  return Math.min(limit, lineStart + Math.max(0, column - 1));
}

export interface TextRange {
  readonly startLineNumber: number;
  readonly startColumn: number;
  readonly endLineNumber: number;
  readonly endColumn: number;
}

/** A half-open UTF-16 range of `text` as a Monaco-shaped range. */
export function utf16RangeOf(text: string, start: number, end: number): TextRange {
  const from = utf16PositionAt(text, start);
  const to = utf16PositionAt(text, end);
  return { startLineNumber: from.line, startColumn: from.column, endLineNumber: to.line, endColumn: to.column };
}
